import { useUser } from '@clerk/clerk-react';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PostHog initialization for the Studio frontend.
 *
 * Wired per the AGENTS.md locked stack — analytics is a Day-1 PR-1
 * obligation. Reads `VITE_POSTHOG_KEY`; when absent, the SDK is a true
 * no-op — no init, no calls, no network. Local dev and CI runs stay
 * free of analytics traffic.
 *
 * Why client-side only: PostHog product analytics is browser-bound
 * (pageviews, funnels, session-context events). Backend lifecycle
 * events are captured in Sentry as breadcrumbs already; if we ever
 * need server-side analytics events (e.g. webhook deliveries), the
 * `posthog-node` package mirrors this pattern from the backend.
 *
 * The host defaults to `https://us.i.posthog.com` (PostHog Cloud US
 * region); override via `VITE_POSTHOG_HOST` for EU customers or a
 * self-hosted PostHog.
 *
 * Sample posture:
 * - autocapture ON for clicks + pageviews (the table-stakes signal).
 * - session_recording OFF — opt in once we know what privacy stance
 *   we want with platform-marketplace listings.
 * - capture_pageview OFF because we drive page changes with React
 *   Router; we'll wire a `usePageviewTracking()` hook in the
 *   follow-up iter to emit explicit `$pageview` events.
 */

let initialized = false;

export function initPostHog(): void {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) {
    initialized = true;
    return;
  }

  // Use `||` instead of `??` so an empty-string env (Vite ships these
  // as `""` rather than `undefined` when present-but-blank in `.env`)
  // falls back to the default host.
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageview: false,
    disable_session_recording: true,
    persistence: 'localStorage',
    loaded: (instance) => {
      if (import.meta.env.MODE === 'development') {
        instance.debug(false); // keep dev console quiet; flip to true when debugging
      }
    },
  });

  initialized = true;
}

/**
 * `true` if `initPostHog()` ran AND a key was present. Lets event-emit
 * sites bail out cheaply when the SDK is inert (instead of paying the
 * cost of the SDK's own no-op guard on every call).
 */
export function isPostHogActive(): boolean {
  return initialized && Boolean(import.meta.env.VITE_POSTHOG_KEY);
}

/**
 * Bind the current user to PostHog so events get a stable distinct_id.
 * Wire this after Clerk completes the sign-in handshake. Safe no-op
 * when PostHog is inert.
 */
export function identifyUser(userId: string, traits: Record<string, unknown> = {}): void {
  if (!isPostHogActive()) return;
  posthog.identify(userId, traits);
}

/**
 * Clear identity on sign-out so the next anonymous session doesn't
 * inherit the previous user's distinct_id.
 */
export function resetUser(): void {
  if (!isPostHogActive()) return;
  posthog.reset();
}

/**
 * Emit a custom event. Use `event_name` in snake_case, e.g.
 * `studio_generate_submitted`. Properties keyed snake_case as well.
 * Safe no-op when PostHog is inert.
 */
export function captureEvent(eventName: string, properties: Record<string, unknown> = {}): void {
  if (!isPostHogActive()) return;
  posthog.capture(eventName, properties);
}

/** Reset helper for tests. Production code never calls this. */
export function resetPostHogForTests(): void {
  initialized = false;
}

/**
 * Pageview hook — watches React Router location and emits a `$pageview`
 * event on every route change (including the initial render).
 *
 * We disable PostHog's autocapture-pageviews in `initPostHog` because
 * the SDK only fires on full page loads — SPA navigations are invisible
 * to it. This hook closes that gap.
 *
 * Safe no-op when PostHog is inert. Mount once near the top of the
 * tree (App.tsx) — only one pageview event per route change is
 * emitted regardless of how deep this is in the tree.
 */
export function usePageviewTracking(): void {
  const location = useLocation();
  const path = location.pathname;
  const search = location.search;

  useEffect(() => {
    if (!isPostHogActive()) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      pathname: path,
      search,
    });
  }, [path, search]);
}

/**
 * Identity bridge — binds the Clerk user to PostHog's distinct_id.
 * Without this, every session's events show up under a fresh anonymous
 * id, making funnel analysis impossible.
 *
 * Watches Clerk's auth state:
 * - `isSignedIn` true → `identifyUser(clerkUserId, { email })`
 * - `isSignedIn` false → `resetUser()` so the anonymous id stays clean
 *
 * Safe no-op when PostHog is inert. Mount once near the top of the
 * tree (App.tsx) inside the ClerkProvider — needs the Clerk context.
 */
export function useClerkPostHogBridge(): void {
  const { isLoaded, isSignedIn, user } = useUser();

  useEffect(() => {
    if (!isLoaded || !isPostHogActive()) return;
    if (isSignedIn && user) {
      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress ?? null,
        created_at: user.createdAt ?? null,
      });
    } else {
      resetUser();
    }
  }, [isLoaded, isSignedIn, user]);
}

export { posthog };
